import { useEffect, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { dropByCacheKey } from 'react-router-cache-route';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { TabsCreators, Tab } from '../store/tabsReducer';
import { getTabInfoFromPath } from '../config/tabRoutes';
import { podeAcessarRota, telaInicial } from '../services/acesso';

// Rotas que não devem abrir tabs (login, cadastro, etc.)
// Match exato para '/' e prefixo para as demais (cobre sub-rotas como /checkout/xxx)
const EXCLUDED_EXACT = ['/'];
const EXCLUDED_PREFIXES = ['/login', '/cadastrar', '/checkout', '/renovar', '/termos-de-uso', '/pdf-viewer', '/cupom-rota-secret'];

export function useTabNavigation() {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.token);
  const todasAsTabs = useAppSelector((state) => state.tabs.tabs);
  const activeTabId = useAppSelector((state) => state.tabs.activeTabId);

  // Aba fixa é a da tela inicial do usuário (o dashboard, ou a primeira tela que ele pode abrir).
  const inicial = telaInicial(token);
  const caminhoInicial = inicial ? inicial.split('?')[0] : '/home';
  const tabs = useMemo(
    () => todasAsTabs.filter((t) => podeAcessarRota(token, t.path)),
    [todasAsTabs, token],
  );

  useEffect(() => {
    const foraDoLugar = todasAsTabs.some((t) => t.id !== '/home' && (t.id === caminhoInicial) === t.closable);
    if (foraDoLugar) {
      dispatch(TabsCreators.fixTab(caminhoInicial));
    }
  }, [todasAsTabs, caminhoInicial, dispatch]);

  // Sincroniza navegação do browser com tabs
  useEffect(() => {
    const pathname = location.pathname;

    if (EXCLUDED_EXACT.includes(pathname) || EXCLUDED_PREFIXES.some((p) => pathname.startsWith(p))) {
      return;
    }

    const tabInfo = getTabInfoFromPath(pathname);
    if (!tabInfo) return;

    const tabId = pathname;
    dispatch(
      TabsCreators.openTab({
        id: tabId,
        path: pathname + location.search,
        title: tabInfo.title,
        icon: tabInfo.icon,
        closable: pathname !== '/home' && pathname !== caminhoInicial,
      }),
    );
  }, [location.pathname, location.search, dispatch, caminhoInicial]);

  const navigateToTab = useCallback(
    (tabId: string) => {
      const tab = tabs.find((t) => t.id === tabId);
      if (tab) {
        dispatch(TabsCreators.setActiveTab(tabId));
        history.push(tab.path);
      }
    },
    [tabs, dispatch, history],
  );

  const closeTab = useCallback(
    (tabId: string) => {
      const tab = tabs.find((t) => t.id === tabId);
      if (!tab || !tab.closable) return;

      // Se estamos fechando a tab ativa, calcula destino antes de dispatch
      if (activeTabId === tabId) {
        const tabIndex = tabs.findIndex((t) => t.id === tabId);
        const remaining = tabs.filter((t) => t.id !== tabId);
        const nextTab = remaining[Math.min(tabIndex, remaining.length - 1)] || remaining[0];
        if (nextTab) {
          history.push(nextTab.path);
        }
      }

      dispatch(TabsCreators.closeTab(tabId));
      // Libera a instancia keep-alive da tela fechada (cacheKey == id da tab == path).
      dropByCacheKey(tabId);
    },
    [tabs, activeTabId, dispatch, history],
  );

  const closeOtherTabs = useCallback(
    (tabId: string) => {
      // Dropa o cache de todas as abas fechaveis exceto a mantida.
      tabs.filter((t) => t.closable && t.id !== tabId).forEach((t) => dropByCacheKey(t.id));
      dispatch(TabsCreators.closeOtherTabs(tabId));
      const tab = tabs.find((t) => t.id === tabId);
      if (tab) {
        history.push(tab.path);
      }
    },
    [tabs, dispatch, history],
  );

  const closeTabsRight = useCallback(
    (tabId: string) => {
      // Se a tab ativa será removida (está à direita), navega para a tab clicada
      const idx = tabs.findIndex((t) => t.id === tabId);
      const activeIdx = tabs.findIndex((t) => t.id === activeTabId);
      const activeTab = tabs[activeIdx];
      if (activeIdx > idx && activeTab?.closable) {
        const tab = tabs.find((t) => t.id === tabId);
        if (tab) history.push(tab.path);
      }
      // Dropa o cache das abas fechaveis à direita da clicada.
      tabs.filter((t, i) => i > idx && t.closable).forEach((t) => dropByCacheKey(t.id));
      dispatch(TabsCreators.closeTabsRight(tabId));
    },
    [tabs, activeTabId, dispatch, history],
  );

  const closeAllTabs = useCallback(() => {
    // Dropa o cache de todas as abas fechaveis.
    tabs.filter((t) => t.closable).forEach((t) => dropByCacheKey(t.id));
    dispatch(TabsCreators.closeAllTabs());
    history.push(inicial ?? '/home');
  }, [tabs, dispatch, history, inicial]);

  return {
    tabs,
    activeTabId,
    navigateToTab,
    closeTab,
    closeOtherTabs,
    closeTabsRight,
    closeAllTabs,
  };
}
